import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  DynamoFile,
  FileCommentTopic,
  FileStatus,
  PopupTextType,
} from '../../types'
import numeral from 'numeral'
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import { fileContainsComment, fileSizeLessThan5MB } from '../../utils/common'
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined'
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import { VirtualItem } from '@tanstack/react-virtual'
import {
  DELETE_FILE,
  isUnhealthyFile,
  renderIndicator,
  renderUnhealthyFileBadge,
} from '../../utils/file_management_utils'

type Props = {
  file: DynamoFile
  virtualItem: VirtualItem
  onFileClick: (file: DynamoFile) => () => void
  selectMultipleFiles: boolean
  addFileIdToSelectedFileRef: (fileId: string) => void
  fileHasChecked: (fileId: string) => boolean
  comments: FileCommentTopic[]
  showPopup: (popup: PopupTextType) => void
  deletePDF: (fileId: string) => Promise<void>
  showRenamePopup: (fileId: string) => void
  movable: boolean
  move?: ({
    id,
    name,
    type,
  }: {
    id: string
    name: string
    type: 'folder' | 'file'
  }) => void
  savePdfsToWorkbook: (file: DynamoFile) => Promise<void>
  isTrimmable: (fileId: string) => any
  trimFile: (fileId: string) => void
  isExportable: (fileId: string) => boolean
  exportPDF: (file: DynamoFile) => () => Promise<void>
  downloadFile: (file: DynamoFile) => () => Promise<void>
}

const MobileFileItem = ({
  file,
  virtualItem,
  addFileIdToSelectedFileRef,
  fileHasChecked,
  onFileClick,
  selectMultipleFiles,
  comments,
  showPopup,
  deletePDF,
  movable,
  showRenamePopup,
  move,
  savePdfsToWorkbook,
  isTrimmable,
  trimFile,
  exportPDF,
  isExportable,
  downloadFile,
}: Props) => {
  return (
    <div
      key={file.fileId}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      <li className="rounded-xl mx-3 mb-3 bg-white border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-200 ease-in-out overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-center min-h-12">
            <div
              className="flex w-full h-full justify-start items-center cursor-pointer group"
              onClick={onFileClick(file)}
            >
              {selectMultipleFiles && (
                <div className="flex items-center pr-3">
                  <input
                    type="checkbox"
                    name="select-all"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                    onChange={() => addFileIdToSelectedFileRef(file.fileId)}
                    checked={fileHasChecked(file.fileId)}
                  />
                </div>
              )}
              {!selectMultipleFiles && (
                <div className="mr-3 flex-shrink-0">
                  {renderIndicator(file.status)}
                </div>
              )}

              <div className="flex-1 min-w-0 mr-4">
                <h3 className="text-sm font-semibold text-gray-900 leading-5 truncate group-hover:text-green-700 transition-colors duration-150">
                  {file.fileName.length >= 25
                    ? `${file.fileName.slice(0, 22)}...`
                    : file.fileName}
                </h3>
                {!isUnhealthyFile(file.status) && (
                  <p className="text-xs font-medium text-gray-500 mt-1">
                    {numeral(file.originalFileSize).format('0b').toString()}
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0">
                {!file.isLocal && (
                  <div className="flex items-center justify-center w-7 h-7 bg-blue-50 rounded-full border border-blue-100">
                    <CloudOutlinedIcon
                      sx={{ fontSize: 14, color: '#2563EB' }}
                    />
                  </div>
                )}
                {!isUnhealthyFile(file.status) &&
                  fileContainsComment(file, comments) && (
                    <div className="flex items-center justify-center w-7 h-7 bg-amber-50 rounded-full border border-amber-100">
                      <ChatBubbleOutlineOutlinedIcon
                        sx={{ fontSize: 14, color: '#D97706' }}
                      />
                    </div>
                  )}
                {isUnhealthyFile(file.status) && (
                  <div className="bg-red-50 text-red-700 text-xs font-semibold px-3 py-1 rounded-full border border-red-200">
                    {renderUnhealthyFileBadge(file.status)}
                  </div>
                )}
              </div>
            </div>
            {file.status !== FileStatus.SUCCEEDED &&
              file.status !== FileStatus.UPLOADING &&
              file.status !== FileStatus.PROCESSING &&
              !selectMultipleFiles && (
                <div className="flex justify-center items-center ml-2">
                  <button
                    className="p-2.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-150 hover:scale-105 border border-red-100"
                    onClick={() =>
                      showPopup({
                        ...DELETE_FILE,
                        onClick: () => deletePDF(file.fileId),
                      })
                    }
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              )}
            {file.status === FileStatus.SUCCEEDED && !selectMultipleFiles && (
              <div className="flex justify-center items-center ml-2">
                <Menu>
                  <MenuButton className="p-2.5 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all duration-150 border border-gray-100">
                    <MoreHorizOutlinedIcon sx={{ fontSize: 18 }} />
                  </MenuButton>
                  <MenuItems
                    anchor="bottom end"
                    className="z-50 mt-2 bg-white rounded-xl border border-gray-200 min-w-52 py-2"
                  >
                    <MenuItem>
                      <button
                        className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors duration-150 first:rounded-t-xl"
                        onClick={() =>
                          showPopup({
                            ...DELETE_FILE,
                            onClick: () => deletePDF(file.fileId),
                          })
                        }
                      >
                        <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                        <span className="font-medium">Delete file</span>
                      </button>
                    </MenuItem>

                    {movable && move && (
                      <MenuItem>
                        <button
                          className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150"
                          onClick={() =>
                            move({
                              id: file.fileId,
                              name: file.fileName,
                              type: 'file',
                            })
                          }
                        >
                          <DriveFileMoveOutlinedIcon sx={{ fontSize: 18 }} />
                          <span className="font-medium">Move</span>
                        </button>
                      </MenuItem>
                    )}

                    {!file.isLocal &&
                      fileSizeLessThan5MB(file.originalFileSize) && (
                        <MenuItem>
                          <button
                            className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-150"
                            onClick={() => savePdfsToWorkbook(file)}
                          >
                            <CloudDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                            <span className="font-medium">Save to local</span>
                          </button>
                        </MenuItem>
                      )}

                    {isTrimmable(file.fileId) && (
                      <MenuItem>
                        <button
                          className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors duration-150"
                          onClick={() => trimFile(file.fileId)}
                        >
                          <ContentCutOutlinedIcon sx={{ fontSize: 18 }} />
                          <span className="font-medium">Trim pages</span>
                        </button>
                      </MenuItem>
                    )}

                    {isExportable(file.fileId) && (
                      <MenuItem>
                        <button
                          className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors duration-150"
                          onClick={exportPDF(file)}
                        >
                          <SimCardDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                          <span className="font-medium">Export modified</span>
                        </button>
                      </MenuItem>
                    )}

                    <MenuItem>
                      <button
                        className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors duration-150"
                        onClick={downloadFile(file)}
                      >
                        <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
                        <span className="font-medium">Export original</span>
                      </button>
                    </MenuItem>

                    <MenuItem>
                      <button
                        className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 last:rounded-b-xl"
                        onClick={() => showRenamePopup(file.fileId)}
                      >
                        <DriveFileRenameOutlineOutlinedIcon
                          sx={{ fontSize: 18 }}
                        />
                        <span className="font-medium">Rename</span>
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            )}
          </div>
        </div>
      </li>
    </div>
  )
}

export default MobileFileItem
